// EchoFilter (ESM) - Canonical single source for both SD_Local and Presentor
// Moving-frame echo prevention with Jaccard similarity, tail bias, and age weighting

class EchoFilter {
	constructor(options = {}) {
		this.threshold = typeof options.threshold === 'number' ? options.threshold : 0.325;
		this.maxUtterances = options.maxUtterances || 8;
		this.ageFadeMs = options.ageFadeMs || 15000;
		this.tailMin = options.tailMin || 4;
		this.tailMax = options.tailMax || 8;
		this.debug = !!options.debug;
		this._utterances = [];
	}

	trackSamUtterance(text) {
		if (!text || !text.trim()) return;
		const raw = text.toLowerCase().trim();
		const tokens = this._tokenize(raw);
		this._utterances.push({ raw, tokens, timestamp: Date.now() });
		if (this._utterances.length > this.maxUtterances) this._utterances.shift();
	}

	reset() {
		this._utterances = [];
	}

	configure(options = {}) {
		if (typeof options.threshold === 'number') this.threshold = options.threshold;
		if (typeof options.maxUtterances === 'number') this.maxUtterances = options.maxUtterances;
		if (typeof options.ageFadeMs === 'number') this.ageFadeMs = options.ageFadeMs;
		if (typeof options.tailMin === 'number') this.tailMin = options.tailMin;
		if (typeof options.tailMax === 'number') this.tailMax = options.tailMax;
		if (typeof options.debug === 'boolean') this.debug = options.debug;
	}

	isSamEcho(inputText) {
		if (!inputText || !inputText.trim()) return false;
		const input = inputText.toLowerCase().trim();
		const inputTokens = this._tokenize(input);
		const now = Date.now();

		const frames = this._buildFrames(inputTokens, this.tailMin, this.tailMax);

		for (let i = this._utterances.length - 1; i >= 0; i--) {
			const u = this._utterances[i];
			const ageMs = now - u.timestamp;
			if (ageMs > this.ageFadeMs * 2) continue;

			const ageWeight = Math.max(0, 1 - (ageMs / this.ageFadeMs));
			const effectiveThreshold = this.threshold * (0.3 + 0.7 * ageWeight);

			if (u.raw.includes(input) || input.includes(u.raw)) {
				if (this.debug) console.log('🔇 EchoFilter: substring/exact');
				return true;
			}

			const tailTokens = u.tokens.slice(Math.max(0, u.tokens.length - this.tailMax));
			for (let k = this.tailMax; k >= this.tailMin && tailTokens.length >= k; k--) {
				const tail = tailTokens.slice(tailTokens.length - k);
				if (this._containsSubsequence(inputTokens, tail)) {
					if (this.debug) console.log('🔇 EchoFilter: tail subsequence');
					return true;
				}
			}

			for (const frame of frames) {
				const similarity = this._jaccard(frame, u.tokens);
				const weighted = similarity * (0.3 + 0.7 * ageWeight);
				if (weighted >= effectiveThreshold) {
					if (this.debug) console.log('🔇 EchoFilter: frame similarity', { similarity, weighted, effectiveThreshold });
					return true;
				}
			}
		}

		return false;
	}

	_tokenize(text) {
		return text.split(/\s+/).filter(Boolean);
	}

	_buildFrames(tokens, minSize, maxSize) {
		const frames = [];
		const n = tokens.length;
		for (let size = Math.min(maxSize, n); size >= Math.min(minSize, n); size--) {
			for (let i = 0; i + size <= n; i++) {
				frames.push(tokens.slice(i, i + size));
			}
		}
		return frames;
	}

	_containsSubsequence(haystackTokens, needleTokens) {
		if (needleTokens.length === 0 || haystackTokens.length === 0) return false;
		outer: for (let i = 0; i + needleTokens.length <= haystackTokens.length; i++) {
			for (let j = 0; j < needleTokens.length; j++) {
				if (haystackTokens[i + j] !== needleTokens[j]) continue outer;
			}
			return true;
		}
		return false;
	}

	_jaccard(aTokens, bTokens) {
		if (!aTokens.length || !bTokens.length) return 0;
		const a = new Set(aTokens);
		const b = new Set(bTokens);
		let intersect = 0;
		for (const t of a) if (b.has(t)) intersect++;
		const union = a.size + b.size - intersect;
		return union > 0 ? intersect / union : 0;
	}
}

export { EchoFilter };
export default EchoFilter;


